<template>
  <div class="row comp-fmg-result my-2">
    <!-- ============================= -->
    <!-- HEADER ROW (clickable only here) -->
    <!-- ============================= -->
    <div class="col-12 grid-row header-click" @click="toggleOpen">
      <div class="grid-name">
        {{ group.name }}
      </div>

      <div class="grid-risky">
        <span :class="{ risky: (group.riskyFriendshipsCount ?? 0) > 0 }">
          {{ group.riskyFriendshipsCount ?? '-' }}
        </span>
        <font-awesome-icon
          :icon="open ? 'chevron-down' : 'chevron-up'"
          class="brand-red"
        />
      </div>
    </div>

    <!-- ============================= -->
    <!-- ACCORDION CONTENT -->
    <!-- ============================= -->
    <div v-show="open" class="col-12 info pt-3">
      <div class="accordion-content">
        <h5>{{ $t('userAdmin.groupDetails') }}</h5>

        <p>
          {{ $t('userAdmin.sectionsCount') }}:
          <strong>{{ group.sections.length }}</strong>
        </p>

        <p>
          {{ $t('userAdmin.zipCodesCount') }}:
          <strong>{{ group.zipCodes.length }}</strong>
        </p>

        <p>
          {{ $t('userAdmin.customerCount') }}:
          <strong>{{ group.customerCount }}</strong>
        </p>

        <p>
          {{ $t('userAdmin.volunteerCount') }}:
          <strong>{{ group.volunteerCount }}</strong>
        </p>

        <p>{{ $t('userAdmin.riskyFriendshipsDescription') }}</p>

        <!-- =========================== -->
        <!-- SECTION EDITOR -->
        <!-- =========================== -->
        <FriendManagementGroupSectionEditor
          :group-id="group.id"
          :active-sections="group.sectionDtos"
          :pool-sections="poolSections"
          :initialRiskyFriendships="group.riskyFriendships"
          @calculate-impact="forwardCalculateImpact"
          @save-sections="forwardSaveSections"
          @delete-fmg="forwardDelete"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import FriendManagementGroupSectionEditor from '../useradmin/FriendManagementGroupSectionEditor.vue'
import { FriendManagementDto, SectionSimpleDto } from '@/types'

export default Vue.extend({
  name: 'FriendManagementGroupSearchResult',

  components: {
    FriendManagementGroupSectionEditor
  },

  props: {
    group: {
      type: Object as () => FriendManagementDto,
      required: true
    },

    // Parent should pass pool sections (FMG=9999)
    poolSections: {
      type: Array as () => SectionSimpleDto[],
      required: true
    }
  },

  data() {
    return {
      open: false
    }
  },

  mounted() {
    this.$bus.$on('clicked-fmg-result', this.handleClicked)
  },

  beforeDestroy() {
    this.$bus.$off('clicked-fmg-result')
  },

  methods: {
    toggleOpen() {
      const prev = this.open
      this.$bus.$emit('clicked-fmg-result', this.$vnode.key)
      this.open = !prev
    },

    handleClicked(uid: any) {
      if (uid !== this.$vnode.key) {
        this.open = false
      }
    },

    //
    // FIXED: Forward all real arguments to parent
    //
    forwardCalculateImpact(
      groupId: number,
      removeIds: number[],
      addIds: number[],
      callback: Function
    ) {
      if (removeIds.length !== 0)
        this.$emit('calculate-impact', groupId, removeIds, addIds, callback)
    },

    forwardSaveSections(
      groupId: number,
      removeIds: number[],
      addIds: number[]
    ) {
      this.$emit('save-sections', groupId, removeIds, addIds)
    },
    forwardDelete(groupId: number) {
      this.$emit('delete-fmg', groupId)
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/_variables.scss';

.comp-fmg-result {
  background: $brand-white;
  border: 2px solid $brand-white;

  &:hover {
    border: 2px solid $brand-red;
  }

  .header-click {
    cursor: pointer;
  }

  .grid-row {
    display: grid;
    grid-template-rows: 60px;
    grid-template-columns: 3fr 160px;
    align-items: center;
  }

  .grid-name {
    grid-column: 1;
    line-height: 60px;
    font-size: 15px;
    font-weight: 700;
    color: $brand-red;
    font-family: 'Signa-Light', Sans-Serif;
    text-transform: uppercase;
  }

  .grid-risky {
    grid-column: 2;
    text-align: right;
    padding-right: 10px;
    line-height: 60px;

    .risky {
      color: $brand-red;
      font-weight: bold;
    }
  }

  .info {
    padding: 15px 20px;
    background: $brand-grey2;
    border-top: 1px solid $brand-grey7;

    .accordion-content {
      font-family: 'Signa-Light', Sans-Serif;
      font-size: 14px;
    }
  }
}
</style>
